import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import React, { memo, useCallback, useEffect, useRef, useState } from 'react';

import FilterForm from '../../../forms/FilterForm/FilterForm';
import { neutral } from '../../../styles/mui/colors';

const popupSx = (theme) => ({
  // By default hide the content
  display: 'block',
  visibility: 'hidden',
  opacity: 0,
  pointerEvents: 'none',

  // Position
  position: 'absolute',
  zIndex: theme.tokens.zIndexPopup,

  // Layout
  minWidth: '300px',
  marginTop: '7px',
  backgroundColor: theme.tokens.matterColorLight,

  // Borders
  borderTop: `1px solid ${theme.tokens.matterColorNegative}`,
  boxShadow: theme.tokens.boxShadowPopup,
  borderRadius: '4px',
  transition: theme.tokens.transitionStyleButton,
});

const popupOpenSx = {
  display: 'block',
  visibility: 'visible',
  opacity: 1,
  pointerEvents: 'auto',
};

const buttonStyle = {
  borderRadius: 200,
  bgcolor: 'white',
  border: `1px solid ${neutral[200]}`, // Set border width to 1px
  '&:hover': {
    bgcolor: 'white',
    boxShadow: 1,
  },
  '&:active': {
    bgcolor: 'white',
  },
  '&:focus': {
    bgcolor: 'white',
  },
  '& .MuiButton-startIcon': {
    color: 'inherit',
  },
  color: 'inherit',
  textOverflow: 'ellipsis',
  overflow: 'hidden',
  whiteSpace: 'nowrap',
};

const formatValues = (selectedOptions, queryParamName, searchMode) => {
  const hasOptionsSelected = selectedOptions && selectedOptions.length > 0;
  const modePrefix = searchMode ? `${searchMode}:` : '';
  const value = hasOptionsSelected ? `${modePrefix}${selectedOptions.join(',')}` : null;
  return { [queryParamName]: value };
};

const FilterPopupComponent = React.forwardRef(
  (
    {
      rootClassName = null,
      className = null,
      popupClassName = null,
      popupSizeClassName = null,
      id,
      label,
      children,
      initialValues = null,
      keepDirtyOnReinitialize = false,
      contentPlacementOffset = 0,
      onSubmit,
      onClear = () => {},
      onCancel = () => {},
      isSelected,
      labelMaxWidth = 0,
      filterConfig = {},
      isOpen,
      setIsOpen,
      showAsPopup,
      ...rest
    },
    ref
  ) => {
    const [contentStyle, setContentStyle] = useState({});

    const filterRef = useRef(null);
    const filterContentRef = useRef(null);

    const handleSubmit = (values) => {
      setIsOpen(false);
      onSubmit(values);
    };

    const handleClear = useCallback(() => {
      setIsOpen(false);
      if (onClear) onClear();
      onSubmit({});
    }, [onClear, onSubmit]);

    const handleCancel = useCallback(() => {
      setIsOpen(false);
      if (onCancel) onCancel();
      onSubmit(initialValues);
    }, [onCancel, onSubmit, initialValues]);

    const handleKeyDown = useCallback(
      (e) => {
        if (e.key === 'Escape') {
          setIsOpen(false);
        }
      },
      [setIsOpen]
    );

    const positionStyleForContent = () => {
      if (filterRef.current && filterContentRef.current) {
        const distanceToRight = window.innerWidth - filterRef.current.getBoundingClientRect().right;
        const labelWidth = filterRef.current.offsetWidth;
        const contentWidth = filterContentRef.current.offsetWidth;
        const contentWidthBiggerThanLabel = contentWidth - labelWidth;
        const renderToRight = distanceToRight > contentWidthBiggerThanLabel;
        const offset = renderToRight
          ? { left: contentPlacementOffset }
          : { right: contentPlacementOffset };
        const minWidth = contentWidth < labelWidth ? { minWidth: labelWidth } : null;
        return { ...offset, ...minWidth };
      }
      return {};
    };

    useEffect(() => {
      const style = positionStyleForContent();
      setContentStyle(style);
      // Optionally, update on window resize
      const handleResize = () => {
        const style = positionStyleForContent();
        setContentStyle(style);
      };
      window.addEventListener('resize', handleResize);
      return () => window.removeEventListener('resize', handleResize);
    }, [filterRef.current, filterContentRef.current, contentPlacementOffset]);

    const popupSizeClasses = popupSizeClassName;

    if (rest.contentRef) {
      delete rest.contentRef;
    }

    return (
      <Box onKeyDown={handleKeyDown} ref={filterRef} {...rest}>
        <Button onClick={() => setIsOpen(true)} sx={buttonStyle} variant="outlined">
          <Typography component="h3" variant="body2">
            {label}
          </Typography>
        </Button>

        {isOpen ? (
          <Box
            className={popupClassName || undefined}
            component="div"
            id={id}
            ref={filterContentRef}
            sx={[
              ...(popupClassName ? [] : [popupSx]),
              ...(isOpen ? [popupOpenSx] : []),
              contentStyle,
            ]}
          >
            <FilterForm
              contentPlacementOffset={contentPlacementOffset}
              id={`${id}.form`}
              initialValues={initialValues}
              keepDirtyOnReinitialize={keepDirtyOnReinitialize}
              onCancel={handleCancel}
              onClear={handleClear}
              onSubmit={handleSubmit}
              paddingClasses={popupSizeClasses}
              showAsPopup={showAsPopup}
            >
              {children}
            </FilterForm>
          </Box>
        ) : null}
      </Box>
    );
  }
);

FilterPopupComponent.displayName = 'FilterPopup';
const FilterPopup = memo(FilterPopupComponent);

export default FilterPopup;
